import { AreaConfig } from "./AreaConfig";
import { Space } from "./AreaLayout";

const ERRORS = {
    SpaceMinSizeViolation: "SpaceMinSizeViolation",
};

export type SpaceWithConfig = {
    space: Space;
    config: AreaConfig;
};

const Layout1axisFunctions = {
    getSpaceWithConfig: (space: Space): SpaceWithConfig => {
        return {
            space,
            config: {
                type: "1axis",
                minSize: 0,
                minParentStartOffset: 0,
                maxSize: undefined,
                minStartOffset: 0,
                minEndOffset: 0,
            },
        };
    },

    getSpacesWithConfig: (items: Space[]): SpaceWithConfig[] => {
        return items.map((item) => Layout1axisFunctions.getSpaceWithConfig(item));
    },

    getFreeSpaceForConfig: (
        root: Space,
        children: Space[] | SpaceWithConfig[],
        config: AreaConfig
    ): Space | undefined => {
        const childrenWithConfig = children.map((child) =>
            "space" in child ? child : Layout1axisFunctions.getSpaceWithConfig(child)
        );

        const sortedChildren = childrenWithConfig.toSorted((a, b) => a.space.start - b.space.start);

        for (let i = 0; i <= sortedChildren.length; i++) {
            const left = i > 0 ? sortedChildren[i - 1].space.end : root.start;
            const right = i < sortedChildren.length ? sortedChildren[i].space.start : root.end;

            const startOffset =
                i > 0
                    ? Math.max(sortedChildren[i - 1].config.minEndOffset, config.minStartOffset)
                    : Math.max(config.minStartOffset, config.minParentStartOffset);

            const endOffset =
                i < sortedChildren.length - 1
                    ? Math.max(sortedChildren[i].config.minStartOffset, config.minEndOffset)
                    : config.minEndOffset;

            const interval = right - left - startOffset - endOffset;

            if (interval >= config.minSize) {
                return {
                    start: left + startOffset,
                    size: interval,
                    end: left + interval + startOffset,
                    type: config.type,
                };
            }
        }
    },

    getFreeSpaceForCoordinate: (
        root: Space,
        children: SpaceWithConfig[],
        coordinate: number,
        freeSpaceAreaConfig?: AreaConfig
    ): Space | undefined => {
        const areaConfig: AreaConfig = freeSpaceAreaConfig ?? {
            maxSize: 0,
            minEndOffset: 0,
            minParentStartOffset: 0,
            minSize: 0,
            minStartOffset: 0,
            type: "1axis",
        };

        if (children.length === 0) {
            const start = Math.max(areaConfig.minStartOffset, areaConfig.minParentStartOffset);
            const end = root.end - areaConfig.minEndOffset;
            const size = end - start;

            return {
                type: root.type,
                start,
                end,
                size,
            };
        }

        const sortedChildren = children.toSorted((a, b) => a.space.start - b.space.start);

        for (let i = 0; i < sortedChildren.length; i++) {
            const left =
                i > 0
                    ? sortedChildren[i - 1].space.end +
                      Math.max(sortedChildren[i - 1].config.minEndOffset, areaConfig.minStartOffset)
                    : root.start +
                      Math.max(areaConfig.minStartOffset, areaConfig.minParentStartOffset);

            const right =
                i < sortedChildren.length - 1
                    ? sortedChildren[i + 1].space.start -
                      Math.max(sortedChildren[i + 1].config.minStartOffset, areaConfig.minEndOffset)
                    : root.end - areaConfig.minEndOffset;

            const child = sortedChildren[i];

            if (coordinate <= child.space.start + child.space.size / 2) {
                const rightOffset = Math.max(child.config.minStartOffset, areaConfig.minEndOffset);

                return {
                    type: "1axis",
                    start: left,
                    end: child.space.start - rightOffset,
                    size: child.space.start - left - rightOffset,
                };
            } else if (coordinate < right) {
                const leftOffset = Math.max(child.config.minEndOffset, areaConfig.minStartOffset);

                return {
                    type: "1axis",
                    start: child.space.end + leftOffset,
                    end: right,
                    size: right - (child.space.end + leftOffset),
                };
            }
        }
    },

    applyConfig: (space: Space, config: AreaConfig): { space: Space; error?: string } => {
        const start = space.start;
        const size = config.maxSize && space.size > config.maxSize ? config.maxSize : space.size;
        const end = start + size;

        let error: string | undefined = undefined;
        if (config.minSize && size < config.minSize) {
            error = ERRORS.SpaceMinSizeViolation;
        }

        return {
            space: { size, start, end, type: config.type },
            error,
        };
    },

    resizeSpace: (space: Space, parentOld: Space, parentNew: Space): Space => {
        const factor = parentNew.size / parentOld.size;
        return {
            size: space.size * factor,
            start: space.start * factor,
            end: space.end * factor,
            type: space.type,
        };
    },
};

export const LayoutFunctions = {
    "1axis": Layout1axisFunctions,
};
